/** Exponential growth curve used by the careers growth chart (2026–2029). */

const CHART_A = 3.4;
const CHART_LEFT = 70;
const CHART_RIGHT = 1010;
const CHART_BOTTOM = 482;
const CHART_TOP = 32;
const CHART_NOW = 0.207;

export const GROWTH_VIEWBOX = "0 0 1040 560";

function chartEase(u: number): number {
  return (Math.exp(CHART_A * u) - 1) / (Math.exp(CHART_A) - 1);
}

function chartPoint(u: number): readonly [number, number] {
  return [
    CHART_LEFT + u * (CHART_RIGHT - CHART_LEFT),
    CHART_BOTTOM - chartEase(u) * (CHART_BOTTOM - CHART_TOP),
  ];
}

function sampleRange(start: number, end: number, count: number): number[] {
  return Array.from(
    { length: count },
    (_: unknown, index: number): number =>
      start + ((end - start) * index) / (count - 1),
  );
}

function catmullPath(points: readonly (readonly [number, number])[]): string {
  const first: readonly [number, number] | undefined = points[0];
  if (first === undefined || points.length < 2) return "";

  let d: string;
  d = `M${first[0].toFixed(1)} ${first[1].toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0: readonly [number, number] = points[Math.max(0, i - 1)] ?? first;
    const p1: readonly [number, number] | undefined = points[i];
    const p2: readonly [number, number] | undefined = points[i + 1];
    const p3: readonly [number, number] =
      points[Math.min(points.length - 1, i + 2)] ?? p2 ?? first;
    if (p1 === undefined || p2 === undefined) continue;
    const c1x: number = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y: number = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x: number = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y: number = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

export const GROWTH_SOLID_PATH: string = catmullPath(
  sampleRange(0, CHART_NOW, 6).map(chartPoint),
);
export const GROWTH_DASHED_PATH: string = catmullPath(
  sampleRange(CHART_NOW, 1, 16).map(chartPoint),
);
export const GROWTH_NOW_X: number = chartPoint(CHART_NOW)[0];
export const GROWTH_NOW_Y: number = chartPoint(CHART_NOW)[1];
